import { useMemo } from 'react';

const STATUS_ORDER = [
  { key: "Complete", label: "Complete", color: "#22c55e", dotClass: "bg-green-500" },
  { key: "In Progress", label: "In Progress", color: "#3b82f6", dotClass: "bg-blue-500" },
  { key: "Not Started", label: "Not Started", color: "#94a3b8", dotClass: "bg-slate-400" },
  { key: "punchList", label: "Punch List", color: "#a855f7", dotClass: "bg-purple-500" },
  { key: "Pending Approval", label: "Pending", color: "#f59e0b", dotClass: "bg-amber-500" },
  { key: "Blocked", label: "Blocked", color: "#ef4444", dotClass: "bg-red-500" },
];

const RADIUS = 45;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function DashboardDonutChart({ projects, onFilterStatus }) {
  const { statusCounts, total } = useMemo(() => {
    const counts = { "Complete": 0, "In Progress": 0, "Not Started": 0, "Pending Approval": 0, "Blocked": 0, punchList: 0 };
    let total = 0;
    for (const p of projects) {
      for (const t of (p.tasks || [])) {
        if (t.isPhaseHeader) continue;
        total++;
        if (t.isPunchList && t.status !== "Complete") {
          counts.punchList++;
        } else {
          counts[t.status] = (counts[t.status] || 0) + 1;
        }
      }
    }
    return { statusCounts: counts, total };
  }, [projects]);

  const segments = useMemo(() => {
    const result = [];
    STATUS_ORDER.reduce((acc, s) => {
      const count = statusCounts[s.key] || 0;
      const dashLen = total > 0 ? (count / total) * CIRCUMFERENCE : 0;
      const prevOffset = acc.length > 0 ? acc[acc.length - 1].cumulative : 0;
      acc.push({ cumulative: prevOffset + dashLen });
      result.push({ ...s, count, dashLen, offset: -prevOffset });
      return acc;
    }, []);
    return result;
  }, [statusCounts, total]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-stone-800 mb-4">Task Status</h3>
      <div className="flex items-center gap-6">
        <svg width="140" height="140" viewBox="0 0 120 120" className="flex-shrink-0">
          <circle cx="60" cy="60" r={RADIUS} fill="none" stroke="#f1f5f9" strokeWidth="14" />
          {segments.map(seg => seg.dashLen > 0 && (
            <circle key={seg.key} cx="60" cy="60" r={RADIUS} fill="none"
              stroke={seg.color} strokeWidth="14"
              strokeDasharray={`${seg.dashLen} ${CIRCUMFERENCE - seg.dashLen}`}
              strokeDashoffset={seg.offset}
              transform="rotate(-90 60 60)" />
          ))}
          <text x="60" y="55" textAnchor="middle" dominantBaseline="central"
            className="text-[22px] font-bold" fill="#1c1917">{total}</text>
          <text x="60" y="72" textAnchor="middle" dominantBaseline="central"
            className="text-[10px]" fill="#64748b">total tasks</text>
        </svg>
        <div className="flex flex-col gap-2 text-xs">
          {segments.map(seg => (
            <button key={seg.key}
              onClick={() => onFilterStatus(seg.key)}
              className="flex items-center gap-2 rounded px-1 -mx-1 py-0.5 cursor-pointer hover:bg-slate-50 transition-colors text-left group">
              <span className={`w-2.5 h-2.5 rounded-full ${seg.dotClass} inline-block`} />
              <span className="text-slate-600">{seg.label}</span>
              <span className="font-semibold text-slate-800 ml-auto pl-3">{seg.count}</span>
              <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-1 rounded hidden group-hover:inline">→ Filter</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
